/* La conversación ancha con los asesores: cómo se lee lo que escriben. Sin React y sin importar nada: se prueba con node. */

export type Turno = { role: "user" | "assistant"; content: string; at?: number; consultas?: { herramienta: string; resumen: string }[] };
export type Asesor = { id: "coach" | "editor"; nombre: string; para: string };

/** Los dos asesores (Rais, 10-oct-2026): el coach para antes y durante; el editor de desarrollo para la reescritura. Los dos pueden escribir. */
export const ASESORES: Asesor[] = [
  { id: "coach", nombre: "Coach literario", para: "Antes y durante: la biblia, el guion, las dudas de una escena. También escribe contigo si se lo pides." },
  { id: "editor", nombre: "Editor de desarrollo", para: "La reescritura: qué falla en el libro entero, el plan y las pasadas." },
];

/** Un trozo de texto con su formato. */
export type Trozo = { t: string; negrita?: boolean; cursiva?: boolean; codigo?: boolean };
export type Bloque =
  | { tipo: "p"; trozos: Trozo[] }
  | { tipo: "h"; nivel: 1 | 2 | 3; trozos: Trozo[] }
  | { tipo: "ul" | "ol"; items: Trozo[][] }
  | { tipo: "cita"; trozos: Trozo[] }
  | { tipo: "tabla"; cabecera: Trozo[][]; filas: Trozo[][][] }
  | { tipo: "codigo"; texto: string }
  | { tipo: "raya" };

const EN_LINEA = /\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*|(?<![\p{L}\p{N}])_([^_\n]+)_(?![\p{L}\p{N}])/gu;

/** **negrita**, *cursiva*, _cursiva_ y `código`, dentro de una línea. Lo que no cierra se deja como texto. */
export function trozosDe(s: string): Trozo[] {
  const fuera: Trozo[] = [];
  let desde = 0;
  for (const m of s.matchAll(EN_LINEA)) {
    if (m.index! > desde) fuera.push({ t: s.slice(desde, m.index) });
    if (m[1] != null) fuera.push({ t: m[1], negrita: true });
    else if (m[2] != null) fuera.push({ t: m[2], codigo: true });
    else fuera.push({ t: (m[3] ?? m[4])!, cursiva: true });
    desde = m.index! + m[0].length;
  }
  if (desde < s.length) fuera.push({ t: s.slice(desde) });
  return fuera;
}

const celdas = (linea: string) => linea.trim().replace(/^\||\|$/g, "").split("|").map((c) => trozosDe(c.trim()));
const ES_SEPARADOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const ES_LISTA = /^\s*[-*•]\s+(.*)$/, ES_NUMERADA = /^\s*\d{1,3}[.)]\s+(.*)$/;

/**
 * El texto de un asesor, en bloques: párrafos, títulos, listas, citas, tablas, código y rayas. Lo justo
 * para que una respuesta larga se lea como en un documento, sin asteriscos a la vista. No interpreta HTML:
 * lo que venga con etiquetas se enseña como texto.
 */
export function bloquesDe(md: string): Bloque[] {
  const lineas = String(md ?? "").replace(/\r\n?/g, "\n").split("\n");
  const fuera: Bloque[] = [];
  let parrafo: string[] = [];
  const cerrar = () => { if (parrafo.length) { fuera.push({ tipo: "p", trozos: trozosDe(parrafo.join("\n")) }); parrafo = []; } };
  for (let i = 0; i < lineas.length; i++) {
    const l = lineas[i];
    if (/^\s*```/.test(l)) {
      cerrar();
      const dentro: string[] = [];
      for (i++; i < lineas.length && !/^\s*```/.test(lineas[i]); i++) dentro.push(lineas[i]);
      fuera.push({ tipo: "codigo", texto: dentro.join("\n") });
      continue;
    }
    if (!l.trim()) { cerrar(); continue; }
    const h = l.match(/^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (h) { cerrar(); fuera.push({ tipo: "h", nivel: Math.min(3, h[1].length) as 1 | 2 | 3, trozos: trozosDe(h[2]) }); continue; }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l)) { cerrar(); fuera.push({ tipo: "raya" }); continue; }
    if (l.includes("|") && i + 1 < lineas.length && ES_SEPARADOR.test(lineas[i + 1]) && lineas[i + 1].includes("-")) {
      cerrar();
      const cabecera = celdas(l), filas: Trozo[][][] = [];
      for (i += 2; i < lineas.length && lineas[i].includes("|") && lineas[i].trim(); i++) filas.push(celdas(lineas[i]));
      i--;
      fuera.push({ tipo: "tabla", cabecera, filas });
      continue;
    }
    const lista = l.match(ES_LISTA), numerada = lista ? null : l.match(ES_NUMERADA);
    if (lista || numerada) {
      cerrar();
      const tipo = lista ? "ul" : "ol", patron = lista ? ES_LISTA : ES_NUMERADA, items: Trozo[][] = [];
      for (; i < lineas.length; i++) {
        const m = lineas[i].match(patron);
        if (m) items.push(trozosDe(m[1]));
        else if (lineas[i].trim() && /^\s{2,}\S/.test(lineas[i]) && items.length) items[items.length - 1] = [...items[items.length - 1], ...trozosDe(" " + lineas[i].trim())];   // la línea que sigue, sangrada, es del mismo punto
        else break;
      }
      i--;
      fuera.push({ tipo, items });
      continue;
    }
    if (/^\s*>\s?/.test(l)) {
      cerrar();
      const dentro: string[] = [];
      for (; i < lineas.length && /^\s*>\s?/.test(lineas[i]); i++) dentro.push(lineas[i].replace(/^\s*>\s?/, ""));
      i--;
      fuera.push({ tipo: "cita", trozos: trozosDe(dentro.join("\n")) });
      continue;
    }
    parrafo.push(l);
  }
  cerrar();
  return fuera;
}

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
/** «10-oct, 18:05» de la fecha de un turno (en milisegundos), en la hora del autor. */
export function cuandoDe(at: number | undefined): string {
  const d = typeof at === "number" ? new Date(at) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getDate()}-${MESES[d.getMonth()]}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** «Ha mirado: el capítulo 3 · las notas»: lo que el asesor consultó del libro antes de contestar. */
export function haMirado(consultas: Turno["consultas"]): string {
  const vistas = [...new Set((consultas ?? []).map((c) => String(c?.resumen ?? "").trim()).filter(Boolean))];
  return vistas.length ? `Ha mirado: ${vistas.join(" · ")}` : "";
}

/** Cuántas palabras ha escrito el asesor en esta conversación: lo que podría acabar copiado en la novela. */
export const palabrasDelAsesor = (hilo: Turno[]): number => hilo.filter((t) => t.role === "assistant").reduce((s, t) => s + (t.content.match(/\S+/g) ?? []).length, 0);

/** Menos que esto no es una biblia: es una respuesta. (El motor aplica el mismo mínimo.) */
export const MINIMO_DE_BIBLIA = 150;
/** ¿Se puede ofrecer «Guardar como biblia» en este turno? Una respuesta del asesor, con fecha (para encontrarla en el hilo guardado) y larga. */
export const valeComoBiblia = (t: Turno): boolean => t.role === "assistant" && typeof t.at === "number" && (t.content.match(/\S+/g) ?? []).length >= MINIMO_DE_BIBLIA;

// ── «Guardar como guion» (encargo 0007, 25-oct-2026) ─────────────────────────────────────────────
//
// El enlace sale bajo una respuesta del coach con forma de guion: capítulos, escenas dentro de ellos y la ficha de alguna.
// La pantalla no puede importar el motor, así que esto es UNA COPIA de lo que él mira (`companion/guardar-guion.mjs`:
// `ETIQUETAS`, `clasificar` y `esGuion`). Una prueba comprueba que los dos dicen lo mismo con los mismos textos: si se
// cambia allí, se cambia aquí.

/** Cómo puede llamar el coach a cada campo de la ficha. La misma lista que `ETIQUETAS` del motor. */
export const ETIQUETAS_DE_CAMPO: Readonly<Record<string, string>> = Object.freeze({
  "pdv": "PDV", "punto de vista": "PDV", "narrador": "PDV", "persona y tiempo": "PDV",
  "objetivo": "Objetivo", "objetivo de la escena": "Objetivo", "funcion": "Objetivo", "funcion de la escena": "Objetivo", "funcion narrativa": "Objetivo", "proposito": "Objetivo", "para que": "Objetivo", "meta": "Objetivo",
  "que pasa": "Qué pasa", "que ocurre": "Qué pasa", "que sucede": "Qué pasa", "lo que pasa": "Qué pasa", "accion": "Qué pasa", "hechos": "Qué pasa", "resumen": "Qué pasa", "sinopsis": "Qué pasa",
  "revela / oculta": "Revela / oculta", "revela y oculta": "Revela / oculta", "revela": "Revela / oculta", "oculta": "Revela / oculta", "que revela": "Revela / oculta", "que oculta": "Revela / oculta", "informacion": "Revela / oculta",
  "siembra / pista": "Siembra / pista", "siembra": "Siembra / pista", "siembras": "Siembra / pista", "pista": "Siembra / pista", "pistas": "Siembra / pista", "siembra y recogida": "Siembra / pista", "semilla": "Siembra / pista",
  "tono y estilo": "Tono y estilo", "tono / estilo": "Tono y estilo", "tono": "Tono y estilo", "estilo": "Tono y estilo", "registro": "Tono y estilo", "atmosfera": "Tono y estilo",
  "como escribirla": "Cómo escribirla", "como escribir": "Cómo escribirla", "como escribir la escena": "Cómo escribirla", "indicaciones": "Cómo escribirla", "indicaciones de oficio": "Cómo escribirla", "oficio": "Cómo escribirla",
  "nota del coach": "Cómo escribirla", "notas del coach": "Cómo escribirla", "nota": "Cómo escribirla", "notas": "Cómo escribirla", "consejo": "Cómo escribirla", "apertura": "Cómo escribirla",
  "gancho final": "Gancho final", "gancho": "Gancho final", "cierre": "Gancho final", "final": "Gancho final", "ultima imagen": "Gancho final", "ultima linea": "Gancho final", "remate": "Gancho final", "cliffhanger": "Gancho final",
});

const clave = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  .replace(/[*_`¿?¡!]/g, "").replace(/\s*\/\s*/g, " / ").replace(/\s+/g, " ").replace(/^[\s.:·—–-]+|[\s.:·—–-]+$/g, "").trim();
const esCampo = (etiqueta: string) => Object.prototype.hasOwnProperty.call(ETIQUETAS_DE_CAMPO, clave(etiqueta));
const G_TITULO = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const G_NEGRITA = /^\s*(?:[-*+•]\s+|\d{1,2}[.)]\s+)?(\*\*|__)(.+?)\1\s*(.*)$/;
const G_LLANA = /^\s*(?:[-*+•]\s+|\d{1,2}[.)]\s+)?([^\s:*#|_`>][^:*#|`]{0,40}?)\s*:\s*(.*)$/;
const G_CAPITULO = /^(?:cap[ií]tulo|chapter|cap\.?|ch\.?)\s*0*(\d{1,3})(?!\d)(?:\s*[·:.—–-]\s*|\s+|$)(.*)$/i;
const G_ACTO = /^(?:acto|parte|act|part)\s+(?:[ivxlc]+|\d{1,2}|primer[oa]?|segund[oa]|tercer[oa]?|cuart[oa]|quint[oa])\b/i;
const G_ESCENA = /^(?:escena|scene)\s*0*(\d{1,3})(?:[.,]0*(\d{1,2}))?(?!\d)(?:\s*[·:.—–-]\s*|\s+|$)(.*)$/i;
const G_NUMERO_DE_ESCENA = /^0*(\d{1,3})\.0*(\d{1,2})(?!\d)(?:\s*[·:—–-]\s*|\s+|$)(.*)$/;
const G_RAYA = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const g_limpio = (s: string) => s.replace(/\*+|`/g, "").replace(/(^|\s)_+|_+(\s|$)/g, "$1$2").trim();

type TipoDeLinea = "vacia" | "raya" | "tabla" | "capitulo" | "acto" | "escena" | "titulo" | "campo" | "etiqueta" | "texto";
function deTitulo(t: string): TipoDeLinea | null {
  const x = g_limpio(t);
  if (G_CAPITULO.test(x)) return "capitulo";
  if (G_ACTO.test(x)) return "acto";
  if (G_ESCENA.test(x) || G_NUMERO_DE_ESCENA.test(x)) return "escena";
  return null;
}
/** Qué es una línea y, si es un título, su nivel (una línea en negrita cuenta como 7). Lo mismo que `clasificar` del motor. */
function tipoDeLinea(cruda: string): { tipo: TipoDeLinea; nivel: number } {
  let l = cruda.replace(/\s+$/, "");
  if (!l.trim() || /^\s*```/.test(l)) return { tipo: "vacia", nivel: 0 };
  if (G_RAYA.test(l)) return { tipo: "raya", nivel: 0 };
  if (/^\s*\|/.test(l)) return { tipo: "tabla", nivel: 0 };
  l = l.replace(/^\s*>\s?/, "");
  const h = l.match(G_TITULO);
  if (h) {
    const nivel = h[1].length, t = g_limpio(h[2]);
    const es = deTitulo(t);
    if (es) return { tipo: es, nivel };
    const [antes, ...despues] = t.split(":");
    return { tipo: esCampo(t) || (despues.length && esCampo(antes)) ? "campo" : "titulo", nivel };
  }
  const b = l.match(G_NEGRITA);
  if (b) {
    let etiqueta = b[2];
    const es = deTitulo(`${etiqueta} ${b[3]}`);
    if (es) return { tipo: es, nivel: 7 };
    if (!b[3] && etiqueta.includes(":")) etiqueta = etiqueta.slice(0, etiqueta.indexOf(":"));
    return { tipo: esCampo(g_limpio(etiqueta)) ? "campo" : "etiqueta", nivel: 7 };
  }
  l = l.replace(/^(\s*)\*(?!\*)(.+?)\*\s*$/, "$1$2").replace(/^(\s*)_(?!_)(.+?)_\s*$/, "$1$2");
  const p = l.match(G_LLANA);
  return { tipo: p && esCampo(p[1]) ? "campo" : "texto", nivel: 0 };
}

/** ¿Tiene forma de guion? Lo mismo que `esGuion` del motor: capítulos, escenas dentro de ellos y algún campo en una escena. */
export function tieneFormaDeGuion(texto: string): boolean {
  let cap = false, esc = false, hay = false, nivelCap = 7;
  const lineas = String(texto ?? "").replace(/^\s*(?:>\s*)?ℹ️?\s*Estás colaborando con una IA[^\n]*\n+/u, "").replace(/<<<PROPUESTA|PROPUESTA>>>/g, "").replace(/\r\n?/g, "\n").split("\n");
  for (const l of lineas) {
    const c = tipoDeLinea(l);
    if (c.tipo === "capitulo") { cap = true; esc = false; nivelCap = c.nivel; }
    else if (c.tipo === "acto") { cap = false; esc = false; }
    else if (c.tipo === "titulo" && c.nivel <= nivelCap) { cap = false; esc = false; }
    else if (c.tipo === "escena") { if (!cap) return false; esc = true; }
    else if (c.tipo === "campo" && esc) hay = true;
  }
  return hay;
}

/** ¿Se puede ofrecer «Guardar como guion» en este turno? Una respuesta del asesor, con fecha (para encontrarla en el hilo guardado) y con forma de guion. */
export const valeComoGuion = (t: Turno): boolean => t.role === "assistant" && typeof t.at === "number" && tieneFormaDeGuion(t.content);

/**
 * Lo que contesta el motor al pedir guardarlo (y, con `hecho`, al guardarlo). `con_ficha_entera`: las escenas con los ocho
 * campos de la ficha escritos; sin ella (un motor de antes) no se dice «todas con su ficha».
 */
export type GuionGuardado = { hecho: boolean; nombre: string; capitulos: number; escenas: number; con_ficha: number; con_ficha_entera?: number; fuera: string[] };

const enMiles = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const enLista = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`);

/**
 * La frase de antes de guardar: cómo se llamará, cuánto lleva, lo que se queda fuera, que no se trae solo y de quién consta.
 * «Todas con su ficha» solo si todas la llevan entera. La pestaña Guion está en el panel de Word: en esta pantalla no hay.
 */
export function fraseDeGuion(d: GuionGuardado): string {
  const capitulos = d.capitulos === 1 ? "un capítulo" : `${enMiles(d.capitulos)} capítulos`;
  const escenas = d.escenas === 1 ? "una escena" : `${enMiles(d.escenas)} escenas`;
  const aMedias = "lo que no esté escrito queda en blanco";
  const conFicha = (d.con_ficha_entera ?? 0) >= d.escenas ? (d.escenas === 1 ? "con su ficha" : "todas con su ficha")
    : !d.con_ficha ? "ninguna con su ficha todavía"
      : d.escenas === 1 ? `con ficha; ${aMedias}` : `${d.con_ficha === 1 ? "una" : enMiles(d.con_ficha)} con ficha; ${aMedias}`;
  const fuera = d.fuera?.length ? ` No caben en el guion y se quedan fuera: ${enLista(d.fuera)}.` : "";
  return `Se guardará como «${d.nombre}» en la carpeta del libro: ${capitulos} y ${escenas}, ${conFicha}.${fuera} No se trae solo: lo traes tú desde el panel de Word, pestaña Guion. Queda apuntado que lo escribió el coach.`;
}
